import { useEffect, useState } from 'react';
import { MANAGER_CODE_LENGTH } from '@odonix/contracts';
import { Alert, Button, Text, TextInput } from '@gravity-ui/uikit';
import { useSession, useSessionStore } from '../../lib/session/context.js';
import { EntryPage } from '../../ui/entry-page.js';
import { Field } from '../../ui/field.js';

/**
 * Вход менеджера: почта, затем шестизначный код из письма. Пароля нет.
 * Текст после отправки — «если адрес заведён»: ответ сервера одинаков для
 * любой почты, и экран не обещает письма, которого может не быть.
 */

function ErrorNote({ error }: { error: string | null }) {
  if (error === null) return null;
  return <Alert theme="danger" message={error} />;
}

function useSecondsUntil(moment: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (moment <= now) return;
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => {
      clearInterval(timer);
    };
  }, [moment, now]);
  return Math.max(0, Math.ceil((moment - now) / 1000));
}

function CodeStep({
  email,
  error,
  notice,
  busy,
  resendAt,
}: {
  email: string;
  error: string | null;
  notice: string | null;
  busy: boolean;
  resendAt: number;
}) {
  const store = useSessionStore();
  const [code, setCode] = useState('');
  const wait = useSecondsUntil(resendAt);

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void store.verifyCode(code.trim());
      }}
      className="flex flex-col gap-4"
    >
      <Text color="secondary">
        Если <b className="text-[var(--g-color-text-primary)]">{email}</b> заведена как почта менеджера, на неё пришло
        письмо с кодом. Код действует 10 минут.
      </Text>
      <Field label="Код из письма" required>
        {(id) => (
          <TextInput
            id={id}
            size="l"
            name="code"
            autoComplete="one-time-code"
            autoFocus
            value={code}
            onUpdate={(value) => {
              setCode(value.replace(/\D/g, '').slice(0, MANAGER_CODE_LENGTH));
            }}
            controlProps={{ inputMode: 'numeric', maxLength: MANAGER_CODE_LENGTH, style: { letterSpacing: '0.4em', textAlign: 'center' } }}
          />
        )}
      </Field>
      {notice === null ? null : <Alert theme="success" message={notice} />}
      <ErrorNote error={error} />
      <div className="flex flex-col gap-2">
        <Button type="submit" view="action" size="l" width="max" loading={busy} disabled={code.length !== MANAGER_CODE_LENGTH}>
          Войти
        </Button>
        <Button type="button" view="outlined" size="l" width="max" disabled={busy || wait > 0} onClick={() => void store.resendCode()}>
          {wait > 0 ? `Новый код — через ${String(wait)} с` : 'Прислать новый код'}
        </Button>
        <Button
          type="button"
          view="flat-secondary"
          size="l"
          width="max"
          disabled={busy}
          onClick={() => {
            store.changeEmail();
          }}
        >
          Другая почта
        </Button>
      </div>
    </form>
  );
}

export function LoginScreen() {
  const store = useSessionStore();
  const session = useSession();
  const [email, setEmail] = useState('');

  if (session.status === 'code') {
    return (
      <EntryPage title="Код из письма" product="Кабинет менеджера">
        <CodeStep email={session.email} error={session.error} notice={session.notice} busy={session.busy} resendAt={session.resendAt} />
      </EntryPage>
    );
  }

  const busy = session.status === 'email' && session.busy;
  const error = session.status === 'email' ? session.error : null;

  return (
    <EntryPage title="Вход" hint="Ваши клиники, сроки подписок и начисления" product="Кабинет менеджера">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void store.requestCode(email.trim());
        }}
        className="flex flex-col gap-4"
      >
        <Field label="Почта" hint="Пароль не нужен: пришлём код на эту почту" required>
          {(id) => (
            <TextInput id={id} size="l" type="email" name="email" autoComplete="username" autoFocus value={email} onUpdate={setEmail} />
          )}
        </Field>
        <ErrorNote error={error} />
        <Button type="submit" view="action" size="l" width="max" loading={busy} disabled={email.trim() === ''}>
          Получить код
        </Button>
      </form>
    </EntryPage>
  );
}
